// src/features/Analytics/components/AnomalyDashboard.jsx
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Stack
} from '@mui/material';
import {
  Warning,
  Error,
  Info,
  CheckCircle,
  TrendingUp,
  WaterDrop,
  LocalGasStation,
  Speed
} from '@mui/icons-material';
import { useI18n } from '../../../i18n/i18n';

export const AnomalyDashboard = ({ anomalyReport, isLoading }) => {
  const { t } = useI18n();

  if (isLoading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography>{t('common_loading')}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!anomalyReport) {
    return (
      <Card>
        <CardContent>
          <Typography>{t('common_loadError')}</Typography>
        </CardContent>
      </Card>
    );
  }

  const { totalAnomalies, criticalCount, highCount, anomalies, recommendations } = anomalyReport;

  if (totalAnomalies === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" color="success.main">
            {t('anomaly_no_issues')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('anomaly_detection_subtitle')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Статистика */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('anomaly_summary')}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Chip 
                icon={<Error />}
                label={`${t('anomaly_critical')}: ${criticalCount}`}
                color="error"
                variant="outlined"
              />
              <Chip 
                icon={<Warning />}
                label={`${t('anomaly_high')}: ${highCount}`} 
                color="warning"
                variant="outlined"
              />
              <Chip 
                icon={<Info />}
                label={t('anomaly_total_found', { count: totalAnomalies })}
                color="info"
                variant="outlined"
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* Рекомендації */}
      {recommendations.length > 0 && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('anomaly_recommendations')}
              </Typography>
              <List dense>
                {recommendations.map((rec, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {rec.priority === 'high' ? <Error color="error" /> : 
                       rec.priority === 'medium' ? <Warning color="warning" /> : 
                       <Info color="info" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={rec.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {rec.description}
                          </Typography>
                          <Typography variant="caption" color="primary">
                            {t('recommendation_actions')}:
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {rec.actions.map((action, i) => (
                              <li key={i}>
                                <Typography variant="caption">
                                  {action}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Останні аномалії */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('anomaly_recent_anomalies')}
            </Typography>
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {anomalies.slice(0, 10).map((anomaly, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={new Date(anomaly.date).toLocaleDateString()}
                      secondary={
                        <Box>
                          {anomaly.anomalies.map((a, i) => (
                            <Typography key={i} variant="caption" display="block">
                              • {a.message}
                            </Typography>
                          ))}
                          <Chip 
                            label={anomaly.totalSeverity}
                            size="small"
                            color={
                              anomaly.totalSeverity === 'critical' ? 'error' :
                              anomaly.totalSeverity === 'high' ? 'warning' : 'default'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < anomalies.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};


